import { type ComponentProps } from 'react';

import type { AgentType } from '@mint-lib/ai-chat';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  typographyVariants,
} from '@mint-lib/tw-components';
import type { AvatarAi } from '@mint-lib/tw-components/ai';
import { cn } from '@mint-lib/tw-utils';

import { AgentLabel } from '../messages/AiMessage/AgentLabel.jsx';

type AccordionViews = 'agentic' | 'aihub' | 'mobile';
type Props = {
  card: AgentType;
  view: AccordionViews;
};

const accordionViews = {
  aihub: {
    height: 'h-14',
    avatarProps: {},
    descriptionClassName: typographyVariants({
      variant: 'bodyLong01',
    }),
    wrapperBorder: {},
  },
  agentic: {
    height: 'h-10',
    avatarProps: {
      avatarSize: 'small' as ComponentProps<typeof AvatarAi>['size'],
      typographyClassName: cn(
        typographyVariants({ variant: 'h6' }),
        'whitespace-nowrap',
      ),
    },
    descriptionClassName: typographyVariants({
      variant: 'bodyLong01',
    }),
    wrapperBorder: {},
  },
  mobile: {
    height: 'h-16',
    avatarProps: {
      typographyClassName: cn(
        typographyVariants({ variant: 'h6', color: 'primary' }),
        'whitespace-nowrap',
      ),
    },
    descriptionClassName:
      'text-[16px] leading-[150%] font-normal text-text-primary',
    wrapperBorder: 'border-none',
  },
} as const;

const AgentAccordion = ({ card, view = 'aihub' }: Props) => {
  return (
    <Accordion type="single" collapsible className="flex w-full">
      <AccordionItem
        value={card.agentName}
        className={cn(accordionViews[view].wrapperBorder)}
      >
        <AccordionTrigger
          className={cn(
            'flex flex-row justify-between',
            accordionViews[view].height,
          )}
        >
          <div className="order-first flex h-full w-full flex-row items-center gap-2">
            <AgentLabel
              agentName={card.agentName}
              accordionView
              {...accordionViews[view].avatarProps}
            />
          </div>
        </AccordionTrigger>
        <AccordionContent>
          <p className={accordionViews[view].descriptionClassName}>
            {card.desc}
          </p>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export { AgentAccordion };
